import { INDUSTRY_SECTORS } from '../config/dataConfig';
import { IndustriesData } from './dataCollector';

// 定义单个行业的计算结果
export interface IndustryPenetrationResult {
  name: string;
  weight: number;
  penetration: number; // 动态计算出的渗透率 (0-1)
  score: number; // 加权后的分数
  contributors: { // 各数据源的得分贡献
    github: number;
    newsapi: number;
    arxiv: number;
  };
}

// 定义最终的计算结果格式
export interface PenetrationResult {
  overallPenetration: number; // 最终的总体渗透率 (%)
  industryBreakdown: IndustryPenetrationResult[];
  lastUpdated: string;
}

// 定义评分模型的权重，方便未来调整
const SCORING_WEIGHTS = {
  github: 0.4,  // 技术实现 - 稍微降低权重
  newsapi: 0.4, // 媒体/市场关注度 - 提高权重反映AI热度
  arxiv: 0.2    // 学术研究
};

// 定义用于标准化的基准值（调整为更合理的现实数值）
const NORMALIZATION_BENCHMARKS = {
  github: { totalRepos: 5000, totalStars: 50000 },    // 大幅降低基准
  newsapi: { volume: 1000 },                          // 降低新闻基准
  arxiv: { paperCount: 500 }                          // 降低论文基准
};

/**
 * 渗透率计算器
 * 接收由DataCollector收集的原始数据，并根据行业框架计算渗透率。
 */
export class PenetrationCalculator {
  
  /**
   * 主计算方法
   * @param industriesData - 从DataCollector获取的行业数据
   * @returns 包含总体渗透率和各行业明细的结果
   */
  public calculate(industriesData: IndustriesData): PenetrationResult {
    let overallScore = 0;
    const industryBreakdown: IndustryPenetrationResult[] = [];

    for (const [sectorKey, sectorConfig] of Object.entries(INDUSTRY_SECTORS)) {
      const dataPoints = industriesData.get(sectorKey);
      
      if (!dataPoints) {
        // 如果某个行业数据缺失，可以跳过或使用静态值
        continue;
      }

      // 1. 计算各数据源的标准化分数 (0-1)
      const githubScore = this.normalize(dataPoints.github?.totalRepos, NORMALIZATION_BENCHMARKS.github.totalRepos);
      const newsapiScore = this.normalize(dataPoints.newsapi?.volume, NORMALIZATION_BENCHMARKS.newsapi.volume);
      const arxivScore = this.normalize(dataPoints.arxiv?.paperCount, NORMALIZATION_BENCHMARKS.arxiv.paperCount);
      
      // 2. 加权计算该行业的渗透率
      const industryPenetration = 
        githubScore * SCORING_WEIGHTS.github +
        newsapiScore * SCORING_WEIGHTS.newsapi +
        arxivScore * SCORING_WEIGHTS.arxiv;
        
      // 3. 计算该行业对总分的贡献
      const weightedScore = industryPenetration * sectorConfig.weight;
      overallScore += weightedScore;

      industryBreakdown.push({
        name: sectorConfig.name,
        weight: sectorConfig.weight,
        penetration: industryPenetration,
        score: weightedScore,
        contributors: {
          github: githubScore,
          newsapi: newsapiScore,
          arxiv: arxivScore
        }
      });
    }

    // 按贡献度排序
    industryBreakdown.sort((a, b) => b.score - a.score);

    return {
      overallPenetration: Math.round(overallScore * 100),
      industryBreakdown,
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * 将原始数据点标准化为0-1之间的分数
   * @param value - 实际值
   * @param benchmark - 用于标准化的基准最大值
   * @returns 0-1之间的分数
   */
  private normalize(value: number | undefined, benchmark: number): number {
    if (typeof value !== 'number' || value < 0) {
      return 0.1; // 给予最小基础分，而不是0
    }
    
    // 使用更温和的标准化方法，不使用开方压缩
    let normalized = value / benchmark;
    
    // 应用对数缓冲，让中等数值有更好的表现
    if (normalized > 0) {
      normalized = Math.log(1 + normalized * 2) / Math.log(3); // 对数缓冲
    }
    
    // 设定更宽松的上限
    return Math.min(normalized, 1.2) * 0.8; // 允许超过基准，但限制在合理范围
  }
} 